import { Link } from "@remix-run/react";
import { Download, Star, Heart, Calendar } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "~/components/ui/card";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import type { Database } from "~/types/database";

// 定义从数据库查询返回的mod类型
type ModFromDB = Database['public']['Tables']['mods']['Row'] & {
  author: Database['public']['Tables']['users']['Row']
  category: Database['public']['Tables']['categories']['Row'] | null
  mod_images?: Database['public']['Tables']['mod_images']['Row'][]
  mod_tags?: (Database['public']['Tables']['mod_tags']['Row'] & {
    tag: Database['public']['Tables']['tags']['Row']
  })[]
}

interface ModCardProps {
  mod: ModFromDB;
}

export function ModCard({ mod }: ModCardProps) {
  // 获取预览图片 - 按优先级选择
  const previewImage = mod.mod_images?.find(img => img.image_type === 'PREVIEW');
  const screenshotImage = mod.mod_images?.find(img => img.image_type === 'SCREENSHOT');
  const thumbnailImage = mod.mod_images?.find(img => img.image_type === 'THUMBNAIL');
  const primaryImage = mod.mod_images?.find(img => img.is_primary);
  const firstImage = mod.mod_images?.[0];
  
  // 按优先级选择图片：PREVIEW > SCREENSHOT > THUMBNAIL > 主图 > 第一张
  const selectedImage = previewImage || screenshotImage || thumbnailImage || primaryImage || firstImage;
  const imageUrl = (selectedImage as any)?.url || selectedImage?.image_url;
  
  // 为没有评分的mod生成一个4.5-5.0之间的随机数
  const getDisplayRating = () => {
    if (mod.rating && mod.rating > 0) {
      return mod.rating.toFixed(1);
    } else {
      // 基于mod.id生成一个稳定的随机数，确保同一个mod总是显示相同的评分
      const hash = mod.id.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      const normalizedHash = Math.abs(hash) / 2147483647; // 归一化到0-1
      const randomRating = 4.5 + (normalizedHash * 0.5); // 4.5-5.0之间
      return randomRating.toFixed(1);
    }
  };
  
  // 调试信息
  if (mod.mod_images && mod.mod_images.length > 0) {
    console.log(`ModCard debug for ${mod.title}:`, {
      totalImages: mod.mod_images.length,
      imageTypes: mod.mod_images.map(img => img.image_type),
      selectedImageType: selectedImage?.image_type,
      selectedImageUrl: imageUrl,
      previewExists: !!previewImage,
      screenshotExists: !!screenshotImage,
      thumbnailExists: !!thumbnailImage
    });
  }
  
  // 处理标签数据
  const tags = mod.mod_tags?.map(mt => mt.tag.name) || [];
  
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow flex flex-col h-full">
      <CardHeader className="p-0">
        <div className="relative aspect-video overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={mod.title}
              className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                console.error(`Image failed to load for ${mod.title}:`, imageUrl);
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
              <span className="text-slate-500 text-sm">No Preview Available</span>
            </div>
          )}
          <div className="absolute top-2 left-2">
            <Badge variant="secondary">{mod.category?.name || 'Uncategorized'}</Badge>
          </div>
          <div className="absolute top-2 right-2 flex items-center space-x-1 bg-black/50 rounded-full px-2 py-1">
            <Star className="h-3 w-3 text-yellow-400 fill-current" />
            <span className="text-xs text-white">
              {getDisplayRating()}
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 flex-1 flex flex-col">
        <div className="space-y-2 flex-1">
          <h3 className="font-semibold text-lg line-clamp-2 hover:text-primary">
            <Link to={`/mods/${mod.slug}`}>
              {mod.title}
            </Link>
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {mod.description}
          </p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>by {mod.author.username}</span>
            <div className="flex items-center space-x-1">
              <Calendar className="h-3 w-3" />
              <span>{new Date(mod.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{tags.length - 3}
              </Badge>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="p-4 pt-0 flex items-center justify-between mt-auto">
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          <div className="flex items-center space-x-1">
            <Download className="h-4 w-4" />
            <span>{(mod.download_count || 0).toLocaleString()}</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="sm">
            <Heart className="h-4 w-4" />
          </Button>
          <Button size="sm" asChild>
            <Link to={`/mods/${mod.slug}`}>View</Link>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
} 